// Grids & Gantts — a grid's Page settings (Stride admin mode only).
//
// What is edited HERE is what belongs to the STRUCTURE AS A WHOLE and has
// nowhere else to live: the structure-wide filter and sort, how the rows are
// loaded, the dropdown filters and the quick buttons.
//
// What is NOT here, deliberately: a folder's rules and the columns. Those are
// changed where they are SHOWN — the amber generator rows inside the folder,
// and the column headings — because a settings page lists every rule of every
// folder stripped of the one thing that says which is which: where it sits.
// See the note above the tabs, and STRUCTURE-PAGES.md §6.
//
// Nothing about this client's structure is in code either way: it is all data
// on the page row.
//
// Every query has a Test beside it that answers with a count, because a
// query nobody has run is a guess.

import { useEffect, useState } from 'react';
import { FONT } from './theme';
import { DEPENDENCY_KINDS, DONE_MODES, FILTER_MODES, SORTS, frozenIdsOf, sortFieldOf } from './config';
import { useIssueLinkTypes } from '@modules/infrastructure/query/referenceQueries';

const DEP_LABELS = { FS: 'Finish \u2192 Start', FF: 'Finish \u2192 Finish', SS: 'Start \u2192 Start', SF: 'Start \u2192 Finish' };
import { SYSTEM_CARRIED } from './clone';
import MultiPick from './MultiPick';
import PinIcon from '../shared/PinIcon';
import GroupSelector from '@platform/ui/GroupSelector/GroupSelector';
import { ADMIN_GROUP } from '@platform/app-shell/frontend/utils/adminAuth';
import { scopeSentence } from './reschedule';
import { RescheduleIcon } from './Rescheduler';

export default function SettingsPanel({
  draft, setDraft, onSave, onRevert, dirty, saving, error, saved,
  ui, S, testZql, fieldList, history, onRollback, loadInfo,
  // The site's work types (a Map keyed by lower-cased name, from
  // useWorkTypes) — what the Cloning tab offers to open up.
  workTypes = null,
  // The other grids on this site ([{ slug, name }], this one left out) and
  // one grid's cloning settings by slug — for "copy the cloning settings
  // from another grid". Either may be absent (a host with no grids).
  listGrids = null, cloningOf = null,
  // THE RESCHEDULER: one grid's rescheduler settings by slug (for "copy from
  // another grid"); whether this host can run it at all (a grid page can; a
  // structure kept elsewhere has no route to move work through); and
  // whether the structure has a Start date column to move.
  rescheduleOf = null, canHostRescheduler = true, hasStartColumn = true,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('structure');
  const [tests, setTests] = useState({});
  const [depsOpen, setDepsOpen] = useState(false);

  const set = (patch) => setDraft({ ...draft, ...patch });

  // The site's link types, for the Dependencies rows. A cached reference
  // query (the folders gantt settings share this cache), asked once when an
  // admin opens this panel — never per row and never per render.
  const linkTypes = useIssueLinkTypes().data || [];
  // One dependency kind. A kind whose link type is cleared is DELETED rather
  // than kept blank, so the config never holds a rule that matches nothing
  // while reading as though it works. Mirrors normalizeDependencies.
  const depCount = Object.keys(draft.gantt.dependencies || {}).length;
  const setDep = (kind, patch) => {
    const deps = { ...(draft.gantt.dependencies || {}) };
    if (patch === null) delete deps[kind];
    else deps[kind] = { ...(deps[kind] || { linkTypeId: '', linkTypeName: '', predecessorSide: 'outward', lagDays: 0 }), ...patch };
    set({ gantt: { ...draft.gantt, dependencies: deps } });
  };
  const move = (key, i, d) => {
    const list = draft[key].slice();
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    set({ [key]: list });
  };

  const runTest = async (zql, id) => {
    if (!zql || !zql.trim()) return;
    setTests((t) => ({ ...t, [id]: 'testing…' }));
    try { const n = await testZql(zql); setTests((t) => ({ ...t, [id]: `${Number(n).toLocaleString()} match` })); }
    catch (e) { setTests((t) => ({ ...t, [id]: `⚠ ${(e && e.message) || 'failed'}` })); }
  };

  const head = (t, sub) => (
    <div style={{ ...S.label, marginTop: 14 }}>{t}{sub ? <span style={{ textTransform: 'none', fontWeight: 400, marginLeft: 8, color: ui.muted }}>{sub}</span> : null}</div>
  );
  const row = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' };
  const small = { ...S.input, padding: '5px 8px', fontSize: 12.5 };
  const mono = { ...small, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' };
  const btn = { ...S.exportBtn, padding: '3px 9px', fontSize: 11.5 };
  const tinyBtn = { ...btn, padding: '1px 7px', fontSize: 11 };
  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setTab(id)} style={{ ...S.pill(tab === id), padding: '4px 11px', fontSize: 12 }}>{label}</button>
  );
  const chk = (label, on, onChange, title) => (
    <label title={title} style={{ ...S.tiny, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} />{label}
    </label>
  );
  const zqlField = (value, onChange, id, label, hint) => (
    <>
      {head(label, hint)}
      <div style={row}>
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={Math.min(5, Math.max(1, Math.ceil((value || '').length / 110)))}
          style={{ ...mono, flex: '1 1 460px', resize: 'vertical', lineHeight: 1.45 }} />
        <button onClick={() => runTest(value, id)} style={btn}>Test</button>
        <span style={S.tiny}>{tests[id] || ''}</span>
      </div>
    </>
  );

  // The field datalist is rendered once by the page, so every field input
  // on this screen shares it and there is never a duplicate id.
  const fields = null;

  // ---- the structure ----------------------------------------------------------
  const structureTab = (
    <div>
      {zqlField(draft.filterZql, (v) => set({ filterZql: v }), 'filter',
        'Filter work items', '— applied at EVERY level, in every folder (blank turns it off)')}

      <div style={row}>
        <label style={{ ...S.tiny, display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 100%' }}>
          What that filter means
          <select value={draft.filterMode} onChange={(e) => set({ filterMode: e.target.value })} style={{ ...small, width: '100%', maxWidth: 560 }}>
            {FILTER_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
      </div>
      <div style={row}>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          Sort the top of every folder by
          <select value={draft.sortRoot} onChange={(e) => set({ sortRoot: e.target.value })} style={{ ...small, width: 160 }}>
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            {/* A field sort is chosen on the amber "Sort by" rule; shown here as it is. */}
            {sortFieldOf(draft.sortRoot) ? <option value={draft.sortRoot}>{sortFieldOf(draft.sortRoot)}</option> : null}
          </select>
        </label>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          Open
          <input type="number" min="0" max="20" value={draft.openDepth} onChange={(e) => set({ openDepth: e.target.value })} style={{ ...small, width: 64 }} />
          level(s) when the page lands
        </label>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          Done work
          <select value={draft.hideDone} onChange={(e) => set({ hideDone: e.target.value })} style={{ ...small, width: 130 }}>
            {DONE_MODES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </label>
        {chk('Cells edit in place', draft.editable, (v) => set({ editable: v }), 'Click a summary, a date or a number to change it')}
        {chk('Show the generator rules in the tree', draft.showRules, (v) => set({ showRules: v }))}
      </div>
      {/* WHICH COLUMNS STAY PUT when the grid scrolls sideways. Unset, it is
          the Key and the Summary; ticking anything here makes it this list. */}
      {head('Pinned at the left', '— these columns stay in view while the others scroll sideways under them (the grid without the timeline)')}
      <div style={row}>
        {(() => {
          const pinned = new Set(frozenIdsOf(draft));
          const cols = (draft.columns || []).filter((c) => c.on !== false);
          const flipPin = (id, on) => {
            const next = new Set(pinned);
            if (on) next.add(id); else next.delete(id);
            set({ frozenColumns: cols.map((c) => c.id).filter((x) => next.has(x)) });
          };
          return (
            <>
              {cols.map((c) => (
                <label key={c.id} title={pinned.has(c.id) ? `${c.label} stays at the left — untick to let it scroll` : `Pin ${c.label} at the left`}
                  style={{ ...S.pill(pinned.has(c.id)), display: 'inline-flex', gap: 5, alignItems: 'center', padding: '3px 10px', fontSize: 12 }}>
                  <input type="checkbox" checked={pinned.has(c.id)} onChange={(e) => flipPin(c.id, e.target.checked)} style={{ margin: 0, accentColor: ui.primary }} />
                  {pinned.has(c.id) ? <PinIcon size={11} color={ui.fg} /> : null}{c.label}
                </label>
              ))}
              {Array.isArray(draft.frozenColumns) ? (
                <button onClick={() => set({ frozenColumns: null })} style={tinyBtn} title="Back to the default: the Key and Summary columns">Reset to Key + Summary</button>
              ) : <span style={S.tiny}>(the default)</span>}
            </>
          );
        })()}
      </div>

      {/* HOW A LONG HEADING FITS. "LINK TO DEBTOR…" names no column; wrapped
          in full, every heading is read whole and the heading row is as
          tall as the longest one needs. */}
      {head('Column headings', '— how a long heading fits its column, for every reader')}
      <div style={row}>
        <select value={draft.headWrap === 'full' ? 'full' : 'fit'} onChange={(e) => set({ headWrap: e.target.value })} style={{ ...small, width: 'auto', maxWidth: 620 }}>
          <option value="full">Wrap in full — every heading whole at full size; the heading row grows to the longest</option>
          <option value="fit">Fit — two or three lines, the type shrinks to fit (may cut a long heading)</option>
        </select>
      </div>

      {head('How the rows are loaded')}
      <div style={row}>
        <span style={S.tiny}>On the server, in one compressed request — every grid, always.</span>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          at most
          <input type="number" min="100" max="200000" step="1000" value={draft.maxRows} onChange={(e) => set({ maxRows: e.target.value })} style={{ ...small, width: 96 }} />
          rows
        </label>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          in pages of
          <input type="number" min="50" max="5000" step="50" value={draft.pageSize} onChange={(e) => set({ pageSize: e.target.value })} style={{ ...small, width: 82 }} />
        </label>
      </div>
      {(
          <div style={row}>
            <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
              Ask about
              <input type="number" min="10" max="1000" step="10" value={draft.walkChunk} onChange={(e) => set({ walkChunk: e.target.value })} style={{ ...small, width: 82 }} />
              parents a query
            </label>
            <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
              , 
              <input type="number" min="1" max="12" value={draft.walkParallel} onChange={(e) => set({ walkParallel: e.target.value })} style={{ ...small, width: 62 }} />
              queries in flight at once
            </label>
            <span style={S.tiny}>The server walks the tree against its own database and answers once, compactly. The knobs shape its queries.</span>
          </div>
        )}

      {head('Timeline')}
      <div style={row}>
        {chk('Offer the Gantt chart', draft.gantt.on, (v) => set({ gantt: { ...draft.gantt, on: v } }),
          'The timeline is a pane of the structure — the same rows, in the same scroller')}
        <span style={S.tiny}>
          Every grid opens with the chart closed. With this on, readers open it themselves
          (📊 in the toolbar) and pick their own interval; with it off, the grid has no chart.
        </span>
      </div>
      <div style={{ ...S.tiny, marginBottom: 4 }}>
        Columns kept while the timeline is on — it shares the width with them, and
        thirteen columns leave it nothing. None ticked = keep them all.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {draft.columns.filter((c) => c.on !== false).map((c) => (
          <label key={c.id} style={{ ...S.tiny, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={(draft.gantt.columns || []).includes(c.id)}
              onChange={(e) => {
                const cur = draft.gantt.columns || [];
                set({ gantt: { ...draft.gantt, columns: e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id) } });
              }} />
            {c.label}
          </label>
        ))}
      </div>

      {/* Collapsed by default and folded like Data scope: four kinds, each
          three controls wide, is a wall of selects in a panel that is read
          top to bottom. Most grids never set one. */}
      <div style={{ marginTop: 14 }}>
        <button onClick={() => setDepsOpen((o) => !o)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
                   cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                   textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>
          <svg width="10" height="10" viewBox="0 0 10 10" style={{ flex: '0 0 auto', transform: depsOpen ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
            <path d="M3 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Dependencies — the arrows between bars
          {depCount > 0 ? <span style={{ textTransform: 'none', fontWeight: 400, marginLeft: 6 }}>({depCount} set)</span> : null}
        </button>
      </div>
      {depsOpen && (
        <>
          <div style={{ ...S.tiny, margin: '6px 0' }}>
            Each kind names the link type that MEANS it on this site, and which end of that
            link is the predecessor. A kind left at <em>— none —</em> draws nothing: there is
            no default, and nothing is guessed from a link&rsquo;s name.
          </div>
          {DEPENDENCY_KINDS.map((kind) => {
            const rule = (draft.gantt.dependencies || {})[kind] || null;
            // Match by id, then by name: a rule saved before link-type ids were
            // normalised carries the NAME as its id, and an id-only compare would
            // show it as unset while it still matches links at runtime.
            const lt = rule
              ? (linkTypes.find((l) => String(l.id) === String(rule.linkTypeId))
                || linkTypes.find((l) => String(l.name || '').toLowerCase() === String(rule.linkTypeName || rule.linkTypeId).toLowerCase()))
              : null;
            // Widths are explicit because S.input is width:100% — inherited, each
            // control took a line of its own and the four kinds filled the page.
            return (
              <div key={kind} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ ...S.tiny, width: 104, flex: '0 0 auto' }}>
                  <strong>{kind}</strong>
                  <span style={{ marginLeft: 6, color: ui.muted }}>{DEP_LABELS[kind]}</span>
                </span>
                <select style={{ ...small, width: 210, flex: '0 0 auto' }}
                  value={lt ? String(lt.id) : ''}
                  onChange={(e) => {
                    const picked = linkTypes.find((l) => String(l.id) === e.target.value);
                    if (!picked) { setDep(kind, null); return; }
                    setDep(kind, { linkTypeId: String(picked.id), linkTypeName: picked.name || '' });
                  }}>
                  <option value="">— none —</option>
                  {linkTypes.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <select style={{ ...small, width: 200, flex: '0 0 auto', opacity: rule ? 1 : 0.5 }}
                  value={rule?.predecessorSide || 'outward'}
                  disabled={!rule}
                  title="Which end of the link is the PREDECESSOR — the bar the arrow leaves"
                  onChange={(e) => setDep(kind, { predecessorSide: e.target.value })}>
                  <option value="outward">{(lt && lt.outward) || 'outward'}</option>
                  <option value="inward">{(lt && lt.inward) || 'inward'}</option>
                </select>
                <input type="number" style={{ ...small, width: 58, flex: '0 0 auto', opacity: rule ? 1 : 0.5 }}
                  value={rule?.lagDays ?? 0} disabled={!rule}
                  onChange={(e) => setDep(kind, { lagDays: Number(e.target.value) || 0 })} />
                <span style={S.tiny}>days lag</span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );

  // ---- the folders ------------------------------------------------------------
  // ---- WHERE FOLDERS AND COLUMNS ARE CHANGED, AND IT IS NOT HERE ----------
  //
  // The Folders and Columns tabs are gone. Not because those things stopped
  // being editable — because this was the WRONG PLACE to edit them, and
  // having a second place at all was the problem.
  //
  // A settings page lists every rule of every folder and every column of the
  // grid, stripped of the one thing that tells you which is which: where it
  // sits. An admin came here to change the thing they were just looking at
  // and had to find it again in a list. Everything that was here is still
  // here, in the place it belongs:
  //
  //   a folder's rules      the amber generator rows, in the folder itself —
  //                         press 📌 and click the rule. Its own editor opens:
  //                         an insert gets a ZQL box with a live Test, a link
  //                         extender gets type/relation/levels, a grouping
  //                         gets a field. "+ Add a rule to this folder" adds
  //                         another one, INSERTS INCLUDED.
  //   a new folder          the "+ Add a folder" line under the structure
  //   a column              its heading — click it for kind, field, width,
  //                         alignment, Σ, move, hide, remove; drag its right
  //                         edge to resize; the + after the last heading adds
  //                         one
  //
  // This is a STANDARD of the custom zebra structure, not a preference of
  // this page: what a structure looks like and how it behaves is settled
  // once, here, and inherited — see STRUCTURE-PAGES.md §6. A structure and a
  // developer are changed together, and both of them are looking at the grid.

  const filtersTab = (
    <div>
      {head('Dropdown filters', '— built from the values actually in the structure')}
      {draft.filters.map((f, i) => (
        <div key={f.id} style={row}>
          <input value={f.label} onChange={(e) => set({ filters: draft.filters.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Label" style={{ ...small, flex: '0 1 180px' }} />
          <input value={f.field} list="wip-fields" onChange={(e) => set({ filters: draft.filters.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })} placeholder="Field name" style={{ ...small, flex: '0 1 220px' }} />
          <button onClick={() => set({ filters: draft.filters.filter((_, j) => j !== i) })} style={{ ...tinyBtn, color: ui.red }}>×</button>
        </div>
      ))}
      <button onClick={() => set({ filters: [...draft.filters, { id: `f${Date.now().toString(36)}`, label: '', field: '' }] })} style={btn}>+ Add a filter</button>

      {head('Quick buttons', '— a ZQL each, run over the structure')}
      {draft.buttons.map((b, i) => (
        <div key={b.id} style={row}>
          <input value={b.caption} onChange={(e) => set({ buttons: draft.buttons.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)) })} placeholder="Caption" style={{ ...small, flex: '0 1 180px' }} />
          <input value={b.zql} onChange={(e) => set({ buttons: draft.buttons.map((x, j) => (j === i ? { ...x, zql: e.target.value } : x)) })} placeholder="ZQL" style={{ ...mono, flex: '1 1 280px' }} />
          {chk('on by default', b.on, (v) => set({ buttons: draft.buttons.map((x, j) => (j === i ? { ...x, on: v } : x)) }))}
          <button onClick={() => runTest(b.zql, `b-${b.id}`)} style={tinyBtn}>Test</button>
          <span style={S.tiny}>{tests[`b-${b.id}`] || ''}</span>
          <button onClick={() => set({ buttons: draft.buttons.filter((_, j) => j !== i) })} style={{ ...tinyBtn, color: ui.red }}>×</button>
        </div>
      ))}
      <div style={row}>
        <button onClick={() => set({ buttons: [...draft.buttons, { id: `b${Date.now().toString(36)}`, caption: '', zql: '', on: false }] })} style={btn}>+ Add a button</button>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          Several on means
          <select value={draft.buttonsMode} onChange={(e) => set({ buttonsMode: e.target.value })} style={{ ...small, width: 150 }}>
            <option value="all">ALL of them (AND)</option>
            <option value="any">ANY of them (OR)</option>
          </select>
        </label>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          A button narrows
          <select value={draft.buttonsScope || 'tree'} onChange={(e) => set({ buttonsScope: e.target.value })} style={{ ...small, width: 230 }}
            title="Every row: the matches anywhere, and the rows leading to them. Inserted rows: each folder's inserted rows, with everything under the ones that pass">
            <option value="tree">every row (and what leads to it)</option>
            <option value="roots">the inserted rows (and all under them)</option>
          </select>
        </label>
      </div>
    </div>
  );

  // ---- cloning ----------------------------------------------------------------
  // WHICH WORK MAY BE CLONED FROM A ROW is the admin's call, by work type. A
  // clone of an epic is the epic, its modules and their tasks — forty
  // creates — and a page starts with nothing cloneable until somebody says
  // which shapes of work are worth that. Readers then get the sheep on
  // those rows and nowhere else.
  const typeNames = (() => {
    const names = new Set((draft.cloneTypes || []).map(String));
    if (workTypes && typeof workTypes.forEach === 'function') workTypes.forEach((t) => { if (t && t.name) names.add(t.name); });
    return [...names].sort((a, b) => a.localeCompare(b));
  })();
  const cloneOn = new Set((draft.cloneTypes || []).map((t) => String(t).toLowerCase()));
  // THE FIELDS A COPY CAN CARRY, by name, once each — what the short list
  // below is picked from. The server's own fields (status, created…) are
  // never carried, so they are never offered.
  const carriedFieldOptions = (() => {
    const seen = new Set();
    const out = [];
    (fieldList || []).forEach((f) => {
      const id = String((f && (f.id || f.key)) || '');
      const name = String((f && f.name) || '').trim();
      if (!name || !(id.startsWith('customfield_') || SYSTEM_CARRIED.includes(id))) return;
      const k = name.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ value: name, label: name });
    });
    return out.sort((a, b) => a.label.localeCompare(b.label));
  })();
  const toggleClone = (name, on) => {
    const cur = (draft.cloneTypes || []).filter((t) => String(t).toLowerCase() !== name.toLowerCase());
    set({ cloneTypes: on ? [...cur, name] : cur });
  };
  // COPY THE CLONING SETTINGS FROM ANOTHER GRID: the other grids are read
  // when the tab is first opened, and a copy lands in the draft — Save keeps
  // it, Revert drops it, exactly like every other change on this panel.
  const [grids, setGrids] = useState(null);         // null = not read yet
  const [copyFrom, setCopyFrom] = useState('');
  const [copyNote, setCopyNote] = useState('');
  useEffect(() => {
    if (!open || (tab !== 'cloning' && tab !== 'rescheduler') || grids !== null || typeof listGrids !== 'function') return undefined;
    let on = true;
    Promise.resolve().then(() => listGrids())
      .then((list) => { if (on) setGrids(Array.isArray(list) ? list : []); })
      .catch(() => { if (on) setGrids([]); });
    return () => { on = false; };
  }, [open, tab, grids, listGrids]);
  const copyCloning = async () => {
    if (!copyFrom || typeof cloningOf !== 'function') return;
    setCopyNote('reading…');
    try {
      const got = await cloningOf(copyFrom);
      set({ cloneTypes: got.cloneTypes, cloneAttention: got.cloneAttention, cloneGroups: got.cloneGroups });
      const name = ((grids || []).find((g) => g.slug === copyFrom) || {}).name || copyFrom;
      setCopyNote(`Copied from ${name}: ${got.cloneTypes.length} work type(s), ${got.cloneAttention.length} important field(s), ${got.cloneGroups.length ? got.cloneGroups.join(', ') : 'everyone'} may clone. Save above to keep it.`);
    } catch (e) {
      setCopyNote(`⚠ ${(e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'that grid could not be read'}`);
    }
  };
  const cloningTab = (
    <div>
      {/* WHO MAY CLONE. A clone is forty creates; the admin decides who may
          start one. Admins always may. */}
      {head('Who may clone', '— the Clone button and the sheep show only for members of these groups')}
      <div style={{ maxWidth: 560, marginBottom: 4 }}>
        <GroupSelector compact groups={draft.cloneGroups || []} onChange={(g) => set({ cloneGroups: g })} />
      </div>
      <div style={{ ...S.tiny, marginBottom: 4 }}>
        {(draft.cloneGroups || []).length
          ? `Only members of ${(draft.cloneGroups || []).join(', ')} (and admins) may clone on this grid.`
          : 'No group named — everyone who may open this grid may clone. Add a group to narrow it.'}
      </div>

      {head('Work types a reader may clone', '— the sheep appears on rows of these types')}
      <div style={{ ...S.tiny, marginBottom: 8, maxWidth: 760 }}>
        A clone copies the work item with every field the create screen accepts, or the
        whole architectural tree under it with every join remade between the copies, as
        many times as asked. The reader picks what to clear. New work starts at the start
        of its workflow.
      </div>
      {typeNames.length === 0 ? <div style={S.tiny}>The site&rsquo;s work types are still loading…</div> : null}
      {typeNames.length ? (
        <div style={{ ...row, marginBottom: 8 }}>
          <button onClick={() => set({ cloneTypes: typeNames.slice() })} style={btn} disabled={cloneOn.size === typeNames.length}
            title="Tick every work type on this site">All</button>
          <button onClick={() => set({ cloneTypes: [] })} style={btn} disabled={!cloneOn.size} title="Untick every work type">None</button>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 900 }}>
        {typeNames.map((name) => (
          <label key={name} style={{ ...S.tiny, display: 'inline-flex', gap: 4, alignItems: 'center', minWidth: 160 }}>
            <input type="checkbox" checked={cloneOn.has(name.toLowerCase())} onChange={(e) => toggleClone(name, e.target.checked)} />
            {name}
          </label>
        ))}
      </div>
      <div style={{ ...S.tiny, marginTop: 8 }}>
        {cloneOn.size ? `${cloneOn.size} type(s) cloneable.` : 'Nothing is cloneable yet.'}
      </div>

      {/* THE SHORT LIST. A clone carries every field the original holds,
          and most of them should come across — but a few are the record
          of THIS piece of work (the hours it took, the day it finished,
          the serial number it was given) and carrying them onto a copy is
          a lie waiting to be believed. The admin names those here, by
          field name; the clone dialog offers them first, as one group that
          ticks with one click, on every grid this structure is. */}
      {head('Important fields to consider clearing', '— offered first in the clone dialog, as one group, whenever a copy would carry them')}
      <div style={{ ...S.tiny, marginBottom: 6, maxWidth: 760 }}>
        Name the fields that record what happened to the ORIGINAL — actual hours, completion dates,
        serial numbers, approvals — so a reader is asked about them before a copy carries them.
        Only the ones the copies would actually carry are shown.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {(draft.cloneAttention || []).map((name) => (
          <span key={name} style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center', border: `1px solid ${ui.border}`, borderRadius: 999, padding: '2px 4px 2px 9px', background: ui.card, color: ui.fg, fontWeight: 600 }}>
            {name}
            <button onClick={() => set({ cloneAttention: (draft.cloneAttention || []).filter((n) => n !== name) })} aria-label={`Remove ${name}`} title="Remove"
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: ui.muted, padding: '0 4px', fontFamily: FONT, lineHeight: 1 }}>×</button>
          </span>
        ))}
        {!(draft.cloneAttention || []).length ? <span style={S.tiny}>None named yet.</span> : null}
      </div>
      {/* PICKED FROM THE SITE'S FIELDS, not typed: a dropdown with a search
          over every field a copy can carry, ticked as many at a time as
          wanted. Typing names (and pasting lists of them) was the old way. */}
      <div style={row}>
        <MultiPick value={draft.cloneAttention || []} options={carriedFieldOptions} ui={ui} S={S} width={360}
          placeholder={fieldList ? 'Choose fields…' : 'reading the fields…'}
          onChange={(names) => set({ cloneAttention: names })} />
        <span style={S.tiny}>{carriedFieldOptions.length ? `search ${carriedFieldOptions.length} fields; tick as many as you want` : ''}</span>
      </div>
      <div style={{ ...S.tiny, marginTop: 4 }}>
        A name the site no longer has is kept (marked in the list) but never shown in the dialog; Save above keeps the list.
      </div>

      {/* THE SAME SETTINGS, FROM ANOTHER GRID. */}
      {typeof listGrids === 'function' ? (
        <>
          {head('Copy the cloning settings from another grid', '— who may clone, the work types and the important fields, into this draft')}
          <div style={row}>
            <select value={copyFrom} onChange={(e) => { setCopyFrom(e.target.value); setCopyNote(''); }} style={{ ...small, width: 'auto', minWidth: 260 }}>
              <option value="">{grids === null ? 'reading the grids…' : (grids.length ? '— pick a grid —' : '— no other grid on this site —')}</option>
              {(grids || []).map((g) => <option key={g.slug} value={g.slug}>{g.name || g.slug} (/grids/{g.slug})</option>)}
            </select>
            <button onClick={copyCloning} style={btn} disabled={!copyFrom}>Copy its cloning settings</button>
            {copyNote ? <span style={{ ...S.tiny, color: copyNote.startsWith('⚠') ? ui.red : ui.fg, fontWeight: 600 }}>{copyNote}</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );

  // ---- the Rescheduler ---------------------------------------------------------
  // A BAR DRAGGED WHOLE TAKES THE WORK UNDER IT ALONG (./reschedule.js). The
  // admin decides three things, and the grid starts with all three shut:
  // whether it is on at all, who besides site admins may use it, and which
  // work types' bars carry the work under them — each with what of that
  // work stays where it is.
  const rsTypes = draft.rescheduleTypes || [];
  const rsByName = new Map(rsTypes.map((t) => [String(t.type).toLowerCase(), t]));
  const rsTypeNames = (() => {
    const all = new Set(rsTypes.map((t) => t.type));
    if (workTypes && typeof workTypes.forEach === 'function') workTypes.forEach((t) => { if (t && t.name) all.add(t.name); });
    // The ticked ones first, then the rest, each A to Z.
    return [...all].sort((a, b) => (rsByName.has(b.toLowerCase()) - rsByName.has(a.toLowerCase())) || a.localeCompare(b));
  })();
  const setRsType = (name, patch) => {
    const k = name.toLowerCase();
    if (patch === null) { set({ rescheduleTypes: rsTypes.filter((t) => String(t.type).toLowerCase() !== k) }); return; }
    const cur = rsByName.get(k);
    set({ rescheduleTypes: cur ? rsTypes.map((t) => (String(t.type).toLowerCase() === k ? { ...t, ...patch } : t)) : [...rsTypes, { type: name, skipDone: true, skipInProgress: false, ...patch }] });
  };
  const [rsCopyFrom, setRsCopyFrom] = useState('');
  const [rsCopyNote, setRsCopyNote] = useState('');
  const copyRescheduler = async () => {
    if (!rsCopyFrom || typeof rescheduleOf !== 'function') return;
    setRsCopyNote('reading…');
    try {
      const got = await rescheduleOf(rsCopyFrom);
      set({ rescheduleOn: got.rescheduleOn, rescheduleGroups: got.rescheduleGroups, rescheduleTypes: got.rescheduleTypes });
      const name = ((grids || []).find((g) => g.slug === rsCopyFrom) || {}).name || rsCopyFrom;
      setRsCopyNote(`Copied from ${name}: ${got.rescheduleOn ? 'on' : 'off'}, ${got.rescheduleTypes.length} work type(s), ${got.rescheduleGroups.length ? `site admins and ${got.rescheduleGroups.join(', ')}` : 'site admins only'}. Save above to keep it.`);
    } catch (e) {
      setRsCopyNote(`⚠ ${(e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'that grid could not be read'}`);
    }
  };
  const th = { textAlign: 'left', fontSize: 11, fontWeight: 700, color: ui.muted, padding: '5px 8px', borderBottom: `1px solid ${ui.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '5px 8px', borderBottom: `1px solid ${ui.border}`, fontSize: 12.5, verticalAlign: 'middle' };
  const reschedulerTab = (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', maxWidth: 820, margin: '8px 0 6px' }}>
        <span style={{ display: 'inline-flex', width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', background: `${ui.blue}1a`, color: ui.blue, flex: '0 0 auto' }}>
          <RescheduleIcon size={18} />
        </span>
        <div style={{ ...S.tiny, lineHeight: 1.55 }}>
          When a bar of one of the work types below is dragged <strong style={{ color: ui.fg }}>whole</strong> on
          the Gantt chart — its Start date and its Due date together — every work item under it in this grid&rsquo;s
          hierarchy moves by the same number of days as soon as the mouse is let go, at every level, not only the
          levels on screen. Dragging only one end of a bar (only the Start date or only the Due date) moves nothing
          else. Every date is saved through the normal work item save: the person&rsquo;s own permissions, History
          and the live update to everybody&rsquo;s open grids all apply. Those bars carry a{' '}
          <RescheduleIcon size={12} color={ui.blue} /> mark that says what a drag will move.
        </div>
      </div>
      {!canHostRescheduler ? (
        <div style={{ ...S.tiny, color: ui.amber, fontWeight: 700, margin: '6px 0' }}>
          This structure is not a grid page (Settings → Content → Grids &amp; Gantts), so the Rescheduler cannot run here.
        </div>
      ) : null}

      {head('Rescheduler on this grid')}
      <div style={row}>
        <label style={{ ...S.pill(!!draft.rescheduleOn), display: 'inline-flex', gap: 7, alignItems: 'center', padding: '5px 12px' }}>
          <input type="checkbox" checked={!!draft.rescheduleOn} onChange={(e) => set({ rescheduleOn: e.target.checked })} style={{ margin: 0, accentColor: ui.blue }} />
          {draft.rescheduleOn ? 'On' : 'Off'}
        </label>
        <span style={S.tiny}>{draft.rescheduleOn ? 'Bars of the work types ticked below carry the work under them.' : 'Off — every drag moves only its own bar (the default).'}</span>
      </div>
      {draft.rescheduleOn && !hasStartColumn ? (
        <div style={{ ...S.tiny, color: ui.red, fontWeight: 700, marginBottom: 6 }}>
          This grid has no Start date column, so no bar has both dates — add a Start date column for the Rescheduler to work.
        </div>
      ) : null}
      {draft.rescheduleOn && !(draft.gantt && draft.gantt.on) ? (
        <div style={{ ...S.tiny, color: ui.amber, fontWeight: 700, marginBottom: 6 }}>
          The Gantt chart is not offered on this grid (Structure → Timeline) — the Rescheduler works on the chart&rsquo;s bars.
        </div>
      ) : null}

      {head('Who may reschedule', '— site admins always may; name groups to let their members too')}
      <div style={{ maxWidth: 560, marginBottom: 4 }}>
        <GroupSelector compact groups={draft.rescheduleGroups || []} onChange={(g) => set({ rescheduleGroups: g })} />
      </div>
      <div style={{ ...S.tiny, marginBottom: 4 }}>
        {(draft.rescheduleGroups || []).length
          ? `Site admins (${ADMIN_GROUP}) and members of ${(draft.rescheduleGroups || []).join(', ')} may reschedule on this grid. Nobody else sees the mark or moves the work under a bar.`
          : `Only site admins (${ADMIN_GROUP}) may reschedule on this grid — the default. Everyone else's drags move their own bar only.`}
      </div>

      {head('Work types whose bars carry the work under them', '— and, for each, which of that work stays where it is')}
      {rsTypeNames.length === 0 ? <div style={S.tiny}>The site&rsquo;s work types are still loading…</div> : (
        <div style={{ maxWidth: 980, maxHeight: 360, overflowY: 'auto', border: `1px solid ${ui.border}`, borderRadius: ui.radiusSm, background: ui.card }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, background: ui.card, zIndex: 1 }}>
              <tr>
                <th style={th}>Work type</th>
                <th style={th}>Moves the work under it</th>
                <th style={th}>Do not move work in Done</th>
                <th style={th}>Do not move work In Progress</th>
                <th style={{ ...th, width: '40%' }}>What a whole drag does</th>
              </tr>
            </thead>
            <tbody>
              {rsTypeNames.map((name) => {
                const t = rsByName.get(name.toLowerCase());
                return (
                  <tr key={name} style={{ background: t ? `${ui.blue}0d` : 'transparent' }}>
                    <td style={{ ...td, fontWeight: t ? 700 : 400 }}>{name}</td>
                    <td style={td}>
                      <input type="checkbox" aria-label={`${name}: moves the work under it`} checked={!!t}
                        onChange={(e) => setRsType(name, e.target.checked ? {} : null)} style={{ accentColor: ui.blue }} />
                    </td>
                    <td style={td}>
                      {t ? <input type="checkbox" aria-label={`${name}: do not move work in a Done status`} checked={!!t.skipDone}
                        onChange={(e) => setRsType(name, { skipDone: e.target.checked })} style={{ accentColor: ui.blue }} /> : null}
                    </td>
                    <td style={td}>
                      {t ? <input type="checkbox" aria-label={`${name}: do not move work in an In Progress status`} checked={!!t.skipInProgress}
                        onChange={(e) => setRsType(name, { skipInProgress: e.target.checked })} style={{ accentColor: ui.blue }} /> : null}
                    </td>
                    <td style={{ ...td, color: ui.muted, fontSize: 11.5 }}>{t ? scopeSentence(t) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ ...S.tiny, marginTop: 6 }}>
        {rsTypes.length ? `${rsTypes.length} work type(s) carry the work under them: ${rsTypes.map((t) => t.type).join(', ')}.` : 'No work type ticked yet — nothing is rescheduled.'}
        {' '}A work item left where it is does not hold back the work under it: each one is judged on its own status.
      </div>

      {typeof listGrids === 'function' && typeof rescheduleOf === 'function' ? (
        <>
          {head('Copy the Rescheduler settings from another grid', '— on or off, who may, and the work types, into this draft')}
          <div style={row}>
            <select value={rsCopyFrom} onChange={(e) => { setRsCopyFrom(e.target.value); setRsCopyNote(''); }} style={{ ...small, width: 'auto', minWidth: 260 }}>
              <option value="">{grids === null ? 'reading the grids…' : (grids.length ? '— pick a grid —' : '— no other grid on this site —')}</option>
              {(grids || []).map((g) => <option key={g.slug} value={g.slug}>{g.name || g.slug} (/grids/{g.slug})</option>)}
            </select>
            <button onClick={copyRescheduler} style={btn} disabled={!rsCopyFrom}>Copy its Rescheduler settings</button>
            {rsCopyNote ? <span style={{ ...S.tiny, color: rsCopyNote.startsWith('⚠') ? ui.red : ui.fg, fontWeight: 600 }}>{rsCopyNote}</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );

  // ---- starting another one ---------------------------------------------------
  // THIS TAB IS THE STANDING INSTRUCTION, and it lives here rather than only
  // in a document because this is where somebody is already standing when
  // the thought occurs: "we want another one of these". A grid is minted in
  // Settings, starts EMPTY, and is built in its own page — the same way
  // every time, with no code — or it starts from this one, by JSON.
  const [copiedJson, setCopiedJson] = useState(false);
  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ structure: draft }, null, 2));
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 1600);
    } catch { /* clipboard denied — swallow */ }
  };
  const newStructureTab = (
    <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
      {head('Starting another grid', 'the same recipe, every time')}
      <div style={{ ...S.tiny, marginBottom: 8, maxWidth: 760 }}>
        This page is not one page — it is <strong style={{ color: ui.fg }}>the Grids &amp; Gantts structure</strong>,
        and every grid on this site renders the same component over its own rules. So a new one is never a
        copy: it is minted in Settings, and it inherits every fix and every feature this one gains from that day on.
      </div>
      <ol style={{ ...S.tiny, margin: '0 0 10px', paddingLeft: 18, maxWidth: 760 }}>
        <li style={{ marginBottom: 4 }}>
          <strong style={{ color: ui.fg }}>Settings → Content → Grids &amp; Gantts</strong> — name it and Create.
          The link is <code>/grids/&lt;slug&gt;</code>; <strong style={{ color: ui.fg }}>Access</strong> sets which
          groups may open it.
        </li>
        <li>
          Open it in admin mode and build the structure in this same panel: add a folder, paste its insert
          query, add the extenders, the groupings and the columns. Save.
        </li>
      </ol>
      {head('Or start from this one')}
      <div style={{ ...S.tiny, marginBottom: 6, maxWidth: 760 }}>
        Copy this structure as JSON and paste it into <strong style={{ color: ui.fg }}>Create from JSON</strong> on
        the Settings card — the new grid opens with these folders, columns and filters under its own name.
      </div>
      <button onClick={copyJson} style={{ ...S.pill(true), marginBottom: 10 }}>{copiedJson ? 'Copied' : 'Copy structure as JSON'}</button>
      <div style={{ ...S.tiny, borderTop: `1px solid ${ui.border}`, paddingTop: 8, maxWidth: 760 }}>
        <strong style={{ color: ui.amber }}>The one rule that keeps this cheap.</strong>{' '}
        Nothing client-specific goes in a file. Every query, field, folder, grouping, sort and column is data on
        the grid, edited right here. If a structure needs the engine to do something new, that goes in the engine,
        with a test, behind a knob that defaults to the old behaviour, so every grid gets it.
      </div>
    </div>
  );

  return (
    <div style={{ border: `1px solid ${ui.amber}`, background: `${ui.amber}0e`, borderRadius: ui.radius, padding: '10px 14px', marginBottom: 14 }}>
      {fields}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={() => setOpen((o) => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.amber, fontFamily: FONT }}>
          {open ? '▾' : '▸'} Page settings
        </button>
        <span style={S.tiny}>admin only — every generator, column and query this structure is built from</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          {saved && !dirty && <span style={{ ...S.tiny, color: ui.green, fontWeight: 700 }}>✓ saved</span>}
          {error && <span style={{ ...S.tiny, color: ui.red, fontWeight: 700 }}>⚠ {error}</span>}
          {dirty && <button onClick={onRevert} style={btn}>Revert</button>}
          <button onClick={() => onSave()} disabled={!dirty || saving} style={{ ...S.pill(true), opacity: !dirty || saving ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            {tabBtn('structure', 'Structure')}
            {tabBtn('filters', 'Filters & buttons')}
            {tabBtn('cloning', 'Cloning')}
            {tabBtn('rescheduler', 'Rescheduler')}
            {tabBtn('new', 'New structure')}
          </div>
          {tab === 'structure' && structureTab}
          {tab === 'filters' && filtersTab}
          {tab === 'cloning' && cloningTab}
          {tab === 'rescheduler' && reschedulerTab}
          {tab === 'new' && newStructureTab}

          {loadInfo ? <div style={{ marginTop: 14, borderTop: `1px solid ${ui.border}`, paddingTop: 8 }}>{loadInfo}</div> : null}
          {history && history.length ? (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={S.tiny}>Previous settings:</span>
              {history.map((h, i) => (
                <button key={h.at || i} onClick={() => onRollback(h)} style={tinyBtn} title="Load these settings into the draft — Save to keep them">
                  ↩ {new Date(h.at).toLocaleString()}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
